/**
 * A tiny in-memory "filesystem" that mirrors the portfolio content, so the
 * shell builtins (ls / cd / cat / pwd) have something real to walk. The tree
 * is built once from src/content.ts — content stays the single source of
 * truth; this just exposes it as files and directories.
 *
 * `cat` returns a string command name to dispatch (e.g. "about") rather than
 * raw text, so a file's contents render with the same rich formatting as the
 * top-level command. Files that map to a renderer set `render`; plain-text
 * files set `text`.
 */

import { content } from "../content";

export interface FsFile {
  type: "file";
  /** Plain text contents (for cat). */
  text?: string;
  /** Dispatch this command name instead, for rich rendering. */
  render?: string;
  /** Marks the downloadable CV. */
  download?: boolean;
}

export interface FsDir {
  type: "dir";
  children: Record<string, FsNode>;
}

export type FsNode = FsFile | FsDir;

export function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function buildTree(): FsDir {
  const experience: Record<string, FsNode> = {};
  for (const job of content.experience) {
    const slug = slugify(job.company);
    experience[`${slug}.txt`] = {
      type: "file",
      render: `experience ${slug}`,
    };
  }

  const projects: Record<string, FsNode> = {};
  for (const proj of content.projects) {
    const slug = slugify(proj.name);
    projects[`${slug}.md`] = { type: "file", render: `projects ${slug}` };
  }

  return {
    type: "dir",
    children: {
      "about.txt": { type: "file", render: "about" },
      "skills.txt": { type: "file", render: "skills" },
      "education.txt": { type: "file", render: "education" },
      "contact.txt": { type: "file", render: "contact" },
      "cv.pdf": { type: "file", download: true },
      experience: { type: "dir", children: experience },
      projects: { type: "dir", children: projects },
      ".secret": {
        type: "dir",
        children: {
          "flag.txt": {
            type: "file",
            text:
              "You found the hidden directory. Curiosity is the most\n" +
              "underrated engineering trait. Let's talk: " +
              content.email,
          },
        },
      },
    },
  };
}

export const ROOT: FsDir = buildTree();

/** Resolve a path (relative to cwd, or absolute) to a node, or null. */
export function resolvePath(
  cwd: string[],
  raw: string,
): { path: string[]; node: FsNode } | null {
  const segments = raw.startsWith("/") ? [] : [...cwd];
  const parts = raw.split("/").filter(Boolean);

  for (const part of parts) {
    if (part === ".") continue;
    if (part === "..") {
      segments.pop();
      continue;
    }
    segments.push(part);
  }

  let node: FsNode = ROOT;
  for (const seg of segments) {
    if (node.type !== "dir" || !(seg in node.children)) return null;
    node = node.children[seg];
  }
  return { path: segments, node };
}

export function dirAt(cwd: string[]): FsDir | null {
  const res = resolvePath([], "/" + cwd.join("/"));
  if (!res || res.node.type !== "dir") return null;
  return res.node;
}

export function pathString(cwd: string[]): string {
  return "/" + cwd.join("/");
}
